import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Avatar, IconButton } from '@mui/material';
import { Phone, PhoneDisabled } from '@mui/icons-material';
import { isAudioUnlocked } from '../../services/soundService';
import { acquireRingtoneLeadership } from '../../services/callCoordination';

interface IncomingCallToastProps {
	open: boolean;
	fromDisplayName?: string | null;
	fromAvatarUrl?: string | null;
	onAccept: () => void;
	onDecline: () => void;
  leftSeconds?: number;
  // Unikalny klucz połączenia – używany do elekcji jednej zakładki grającej dzwonek.
  callKey?: string | null;
  /** Czy to telekonferencja (więcej niż jeden zaproszony). */
  isConference?: boolean;
  /** Liczba uczestników razem z dzwoniącym (do podpisu „dzwoni do Ciebie + 3 osób”). */
  participantsCount?: number;
}

const RING_SOURCES = [
	'/sounds/ring.mp3',
	'/sounds/ring.ogg',
	'https://cdn.pixabay.com/download/audio/2022/03/15/audio_9b2b6a7ff1.mp3?filename=telephone-ring-03-111397.mp3'
];

const IncomingCallToast: React.FC<IncomingCallToastProps> = ({ open, fromDisplayName, fromAvatarUrl, onAccept, onDecline, leftSeconds, callKey, isConference, participantsCount }) => {
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const [audioReady, setAudioReady] = useState(false);
	const [needsTap, setNeedsTap] = useState(false);
	// Czy ta zakładka jest „liderem” odtwarzającym dzwonek (przy wielu otwartych zakładkach).
	const [isRingtoneLeader, setIsRingtoneLeader] = useState(false);
	const releaseLeadershipRef = useRef<null | (() => void)>(null);

	// Elekcja lidera dzwonka – tylko jedna zakładka wygrywa dla danego callKey.
	useEffect(() => {
		let cancelled = false;
		if (!open) {
			setIsRingtoneLeader(false);
			if (releaseLeadershipRef.current) {
				try { releaseLeadershipRef.current(); } catch { /* ok */ }
				releaseLeadershipRef.current = null;
			}
			return;
		}
		// Jeżeli nie mamy klucza, zachowaj dotychczasowe zachowanie (pojedyncza zakładka i tak gra).
		if (!callKey) {
			setIsRingtoneLeader(true);
			return;
		}
		(async () => {
			const release = await acquireRingtoneLeadership(callKey);
			if (cancelled) {
				if (release) { try { release(); } catch { /* ok */ } }
				return;
			}
			if (release) {
				releaseLeadershipRef.current = release;
				setIsRingtoneLeader(true);
			} else {
				setIsRingtoneLeader(false);
			}
		})();
		return () => {
			cancelled = true;
			if (releaseLeadershipRef.current) {
				try { releaseLeadershipRef.current(); } catch { /* ok */ }
				releaseLeadershipRef.current = null;
			}
		};
	}, [open, callKey]);

	useEffect(() => {
		if (!open || !isRingtoneLeader) {
			try { audioRef.current?.pause?.(); } catch {}
			// Reset stanów dźwięku – UI w „nie-liderze” nie pokazuje komunikatów o audio.
			if (!isRingtoneLeader) {
				setAudioReady(false);
				setNeedsTap(false);
			}
			return;
		}
		let cancelled = false;
		(async () => {
			try {
				if (!audioRef.current) {
					const a = new Audio();
					a.loop = true;
					audioRef.current = a;
				}
				const a = audioRef.current!;
				let played = false;
				for (const src of RING_SOURCES) {
					if (cancelled) return;
					a.src = src;
					try {
						if (!isAudioUnlocked()) throw new Error('locked');
						await a.play();
						played = true;
						setAudioReady(true);
						break;
					} catch {}
				}
				if (!played) setNeedsTap(true);
			} catch { setNeedsTap(true); }
		})();
		return () => { cancelled = true; };
	}, [open, isRingtoneLeader]);

	const handleEnableSound = async () => {
		if (!isRingtoneLeader) return;
		try {
			if (!audioRef.current) {
				const a = new Audio(RING_SOURCES[0]);
				a.loop = true;
				audioRef.current = a;
			}
			await audioRef.current.play();
			setNeedsTap(false);
			setAudioReady(true);
		} catch {}
	};

	if (!open) return null;

	return (
		<Box
			onClick={() => { if (needsTap && isRingtoneLeader) handleEnableSound(); }}
			sx={{
				position: 'fixed', inset: 0, zIndex: 2000,
				display: 'flex', alignItems: 'center', justifyContent: 'center',
				bgcolor: 'rgba(0,0,0,0.45)'
			}}
		>
			<Box sx={{
				display: 'flex', alignItems: 'center', gap: 2,
				p: 2, pr: 2.5,
				bgcolor: 'background.paper', borderRadius: 2,
				boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
				minWidth: 360, maxWidth: '90%'
			}}>
				{/* Avatar z pulsującą obwódką */}
				<Box sx={{ position: 'relative' }}>
					<Avatar src={fromAvatarUrl || undefined} sx={{ width: 64, height: 64 }}>
						{(fromDisplayName || '?').slice(0,1).toUpperCase()}
					</Avatar>
					<Box sx={{
						position: 'absolute', inset: -6, borderRadius: '50%',
						border: '3px solid rgba(59,130,246,0.35)',
						animation: 'pulse 1.6s ease-out infinite',
						'@keyframes pulse': {
							'0%': { transform: 'scale(1)', opacity: 1 },
							'100%': { transform: 'scale(1.6)', opacity: 0 }
						}
					}} />
				</Box>

				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
						{isConference ? 'Telekonferencja' : 'Połączenie przychodzące'}
						{typeof leftSeconds === 'number' ? ` · 00:${String(Math.max(0, leftSeconds)).padStart(2, '0')}` : ''}
					</Typography>
					<Typography variant="h6" noWrap sx={{ fontWeight: 700 }}>{fromDisplayName || 'Użytkownik'}</Typography>
					{isConference && (
						<Typography variant="caption" color="text.secondary">
							{typeof participantsCount === 'number' && participantsCount > 0
								? `Łącznie uczestników: ${participantsCount}`
								: 'Połączenie grupowe'}
						</Typography>
					)}
					{isRingtoneLeader && needsTap && (
						<Typography variant="caption" color="text.secondary">Kliknij, aby włączyć dźwięk…</Typography>
					)}
					{isRingtoneLeader && !needsTap && !audioReady && (
						<Typography variant="caption" color="text.secondary">Łączenie…</Typography>
					)}
				</Box>

				{/* Przyciski akcji */}
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
					<IconButton onClick={onDecline} color="error" sx={{
						bgcolor: '#fee2e2', color: '#991b1b', width: 56, height: 56,
						'&:hover': { bgcolor: '#fecaca' }, borderRadius: '50%'
					}}>
						<PhoneDisabled />
					</IconButton>
					<IconButton onClick={onAccept} color="success" sx={{
						bgcolor: '#dcfce7', color: '#166534', width: 64, height: 64,
						borderRadius: '50%', boxShadow: '0 6px 14px rgba(22,101,52,0.35)'
					}}>
						<Phone sx={{ animation: 'wiggle 1.2s ease-in-out infinite', '@keyframes wiggle': {
							'0%,100%': { transform: 'rotate(0deg)' },
							'15%': { transform: 'rotate(18deg)' },
							'30%': { transform: 'rotate(-14deg)' },
							'45%': { transform: 'rotate(10deg)' },
							'60%': { transform: 'rotate(-6deg)' },
							'75%': { transform: 'rotate(3deg)' }
						}}} />
					</IconButton>
				</Box>
			</Box>
		</Box>
	);
};

export default IncomingCallToast;
